/**
 * Борлуулалтын CSV экспорт — `GET /reports/sales/export`.
 *
 * Шийдвэр (BACKLOG, 2026-08-21): xlsx биш, UTF-8 BOM-той CSV —
 * `exceljs` нэмэхгүй. BOM-гүй бол Excel кириллийг эвдэж харуулна.
 *
 * Нэг мөр = нэг зарсан гутал (шийдвэр 2026-09-18). Нягтлан Excel-д
 * өөрөө бүлэглэнэ.
 *
 * ⚠️ Мөнгө `450000.00` — таслалгүй, аравтын цэгтэй. Энэ нь ӨГӨГДӨЛ,
 *    дэлгэцийн формат биш (CLAUDE.md №14 дэлгэцэд хамаарна). Таслалтай
 *    бол Excel текст гэж уншиж нийлбэр бодохгүй.
 * ⚠️ Худалдан авагчийн нэр, утас ОРОХГҮЙ (хувийн мэдээлэл). Загварын нэр
 *    ОРОХГҮЙ (UI-д зөвхөн баркод).
 */
import { Decimal } from 'decimal.js';
import { ulaanbaatarDateKey, ulaanbaatarParts } from './revenue.js';
/**
 * Төлбөрийн хэлбэрийн монгол нэр.
 * ⚠️ Вэб ба сервер ХУВААЛЦАНА — CSV ба дэлгэц нэг нэр хэрэглэнэ.
 */
export const PAYMENT_MN = {
    CASH: 'Бэлэн',
    CARD: 'Карт',
    QPAY: 'QPay',
    TRANSFER: 'Шилжүүлэг',
    VOUCHER: 'Эрхийн бичиг',
    MIXED: 'Холимог',
    EXTERNAL_POS: 'Гадны касс',
    /** Аль апп болохыг `order_payments.provider` хэлнэ. */
    CREDIT_APP: 'Зээлийн апп',
    /** Дэлгүүрийн өөрийн зээл — `installment.ts`. */
    INSTALLMENT: 'Хувь зээл',
    /** Буцаасан гутлын үнэ шинэ худалдан авалтад тооцогдсон — мөнгө биш (`payment.ts`). */
    EXCHANGE: 'Солилцоо',
};
export const SALE_CHANNEL_MN = {
    STORE: 'Дэлгүүр',
    ONLINE: 'Онлайн',
    WHOLESALE: 'Бөөний',
};
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;
/**
 * CSV нүд. Таслал, хашилт, мөр шилжилттэй бол хашилтад хийнэ.
 *
 * ⚠️ Томьёо шургуулалт: `=`, `+`, `-`, `@`, TAB, CR-ээр эхэлсэн ТЕКСТИЙГ
 *    Excel томьёо болгож ажиллуулна (салбар, ажилтны нэрийг хэрэглэгч
 *    оруулдаг). Урд нь `'` нэмж текст болгоно. Цэвэр тоо (`-5000.00` —
 *    алдагдалтай ашиг) хамаарахгүй, эс тэгвээс Excel тоо гэж танихгүй.
 */
export function csvCell(value) {
    let v = value ?? '';
    if (/^[=+\-@\t\r]/.test(v) && !PLAIN_NUMBER.test(v))
        v = `'${v}`;
    return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
/** UTF-8 BOM. Эх кодод харагдахгүй тэмдэгт үлдээхгүйн тулд кодоор. */
const BOM = String.fromCharCode(0xfeff);
/** BOM + CRLF мөрүүд (Excel Windows дээр CRLF хүлээдэг). */
export function toCsv(header, rows) {
    const line = (cells) => cells.map(csvCell).join(',');
    return `${BOM}${[line(header), ...rows.map(line)].join('\r\n')}\r\n`;
}
/** `2026-09-18 10:37` — УБ-ийн цагаар. */
export function ulaanbaatarDateTimeText(at) {
    const p = ulaanbaatarParts(at);
    // УБ-ийн offset бүхэл цаг тул цагийн доторх минут UTC-тэй ижил
    const min = Math.floor((at.getTime() % 3_600_000) / 60_000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${ulaanbaatarDateKey(at)} ${pad(p.hour)}:${pad(min)}`;
}
const money = (v) => new Decimal(v).toFixed(2);
/**
 * CSV-ийн толгой ба мөрүүд.
 *
 * ⚠️ `showCost` false бол Өртөг, Ашиг, Урамшуулал багана БҮХЭЛДЭЭ
 *    байхгүй — хоосон нүд биш. Хоосон нүд «өртөггүй зарагдсан» гэсэн
 *    утгатай тул эрхгүй хүнд төөрөгдөл үүсгэнэ.
 */
export function salesExportCsv(lines, showCost) {
    const header = [
        'Огноо', 'Захиалга', 'Салбар', 'Худалдагч', 'Серийн дугаар',
        'Төлбөр', 'Суваг', 'Үнэ', 'Хөнгөлөлт', 'Дүн',
        ...(showCost ? ['Өртөг', 'Ашиг', 'Урамшуулал'] : []),
    ];
    const rows = lines.map((l) => [
        ulaanbaatarDateTimeText(l.soldAt),
        l.orderCode,
        l.storeName,
        l.sellerName,
        l.serial,
        PAYMENT_MN[l.payment] ?? l.payment,
        SALE_CHANNEL_MN[l.channel] ?? l.channel,
        money(l.priceMnt),
        money(l.discountMnt),
        money(l.lineTotalMnt),
        ...(showCost
            ? [
                l.costMntAtSale === null ? null : money(l.costMntAtSale),
                l.costMntAtSale === null ? null : new Decimal(l.lineTotalMnt).minus(l.costMntAtSale).toFixed(2),
                money(l.commissionMnt),
            ]
            : []),
    ]);
    return toCsv(header, rows);
}
//# sourceMappingURL=sales-export.js.map